// The corpus PRIOR (plan §6): how far out a candidate is, measured as the
// mean surprisal, in bits, of the roman transitions it writes, under a
// bigram model of the Jazz 1460 (src/data/romanBigrams.json, built by
// scripts/build-roman-bigrams.ts over LOCAL-key romans). A diatonic ii–V–I
// costs ~3 bits a step; a Coltrane cycle ~9. The adventure dial reads this
// number and nothing else — it never rewards being weird for its own sake,
// it only says which band a candidate falls in.

import table from '@/data/romanBigrams.json';

interface BigramTable {
  transitions: number;
  unigrams: Record<string, number>;
  bigrams: Record<string, number>;
}

const TABLE = table as BigramTable;
const VOCABULARY = Object.keys(TABLE.unigrams).length || 1;

const BARE_DEGREE = /^[♭♯]?(I|II|III|IV|V|VI|VII)$/;

/**
 * A roman as the TABLE spells it. The analysis prints a triad the way a lead
 * sheet writes one ("I", "V+") and a hybrid with no third as a sus ("Vsus"),
 * but the table was counted over iReal charts, whose parser turns a bare
 * triad into a maj7 and an augmented one into a maj7♯5 (both the ∆ family),
 * and it predates the sus reading. Looked up verbatim, a step into a triad
 * cost more than a transition nobody ever wrote. So a roman the table knows
 * passes through, and one it does not is read as the family it counted:
 * a bare degree and a "+" as ∆, a sus as the dominant it is.
 */
export function priorToken(roman: string): string {
  if (Object.hasOwn(TABLE.unigrams, roman)) return roman;
  if (BARE_DEGREE.test(roman)) return `${roman}∆`;
  if (roman.endsWith('+')) return `${roman.slice(0, -1)}∆`;
  if (roman.endsWith('sus')) return `${roman.slice(0, -3)}7`;
  return roman;
}

/** −log2 P(next | prev), add-one smoothed over the observed vocabulary, so an
 *  unseen transition is finite (and expensive) rather than infinite. */
export function transitionBits(prev: string, next: string): number {
  const from = priorToken(prev);
  const to = priorToken(next);
  const joint = TABLE.bigrams[`${from}→${to}`] ?? 0;
  const context = TABLE.unigrams[from] ?? 0;
  return -Math.log2((joint + 1) / (context + VOCABULARY));
}

/** Mean bits over consecutive romans; NaN when there is no transition. */
export function meanSurprisal(romans: readonly string[]): number {
  const steps = romans.filter(Boolean);
  if (steps.length < 2) return NaN;
  let sum = 0;
  for (let i = 1; i < steps.length; i++) sum += transitionBits(steps[i - 1], steps[i]);
  return sum / (steps.length - 1);
}

/**
 * The adventure BANDS, in bits — where a candidate's transitions sit relative
 * to what the corpus itself does. Calibrated on the Jazz 1460's own 51,059
 * transitions (Sep 2026): median 3.1 bits, 75th percentile 4.6, 90th 6.5,
 * 95th 7.4, 99th 9.2; a never-seen transition costs ~9.3. So "inside" is the
 * three quarters of steps every standard makes, "stretched" reaches the 90th
 * percentile, "out" is the rare-but-attested tail, "free" is anything. The
 * landmarks are pinned in surprisal.test.ts so a rebuilt table that moves
 * them shows up by name.
 */
export type AdventureBand = 'inside' | 'stretched' | 'out' | 'free';
export const ADVENTURE_BANDS: readonly AdventureBand[] = ['inside', 'stretched', 'out', 'free'];
export const ADVENTURE_CEILING: Record<AdventureBand, number> = {
  inside: 4.6,
  stretched: 6.5,
  out: 8.5,
  free: Number.POSITIVE_INFINITY,
};

export function adventureBandOf(bits: number): AdventureBand {
  if (!Number.isFinite(bits)) return 'inside';
  if (bits <= ADVENTURE_CEILING.inside) return 'inside';
  if (bits <= ADVENTURE_CEILING.stretched) return 'stretched';
  if (bits <= ADVENTURE_CEILING.out) return 'out';
  return 'free';
}

export const BIGRAM_TABLE_SIZE = { transitions: TABLE.transitions, romans: VOCABULARY };
